import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Clock, Plus } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/shared/EmptyState';
import { ErrorAlert } from '@/components/shared/ErrorAlert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { supabase } from '@/lib/supabase';
import { TimerWidget } from '@/features/time-tracking/components/TimerWidget';
import { ManualEntryDialog } from '@/features/time-tracking/components/ManualEntryDialog';

interface TimeEntry {
  id: string;
  date: string;
  duration_minutes: number;
  task_title: string | null;
  project_title: string | null;
  description: string | null;
}

interface Project {
  id: string;
  title: string;
}

export function TimeTrackingPage() {
  const [projectFilter, setProjectFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showManualEntry, setShowManualEntry] = useState(false);

  const { data: entries, isLoading, error, refetch } = useQuery({
    queryKey: ['time-entries'],
    queryFn: async () => {
      const { data, error: fetchError } = await supabase
        .from('time_entry')
        .select('*')
        .order('logged_at', { ascending: false });
      if (fetchError) throw fetchError;
      return (data ?? []).map((d) => ({
        id: d.id,
        date: d.logged_at,
        duration_minutes: d.duration_minutes,
        task_title: '',
        project_title: '',
        description: d.description,
      })) as TimeEntry[];
    },
  });

  const { data: projects } = useQuery({
    queryKey: ['projects-list'],
    queryFn: async () => {
      const { data, error: fetchError } = await supabase
        .from('project')
        .select('id, name')
        .order('name');
      if (fetchError) throw fetchError;
      return (data ?? []).map((d) => ({ id: d.id, title: d.name })) as Project[];
    },
  });

  const filtered = useMemo(() => {
    if (!entries) return [];
    return entries.filter((e) => {
      if (projectFilter !== 'all' && e.project_title !== projectFilter) return false;
      if (dateFrom && e.date < dateFrom) return false;
      if (dateTo && e.date > dateTo) return false;
      return true;
    });
  }, [entries, projectFilter, dateFrom, dateTo]);

  const dailyTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    for (const entry of filtered) {
      totals[entry.date] = (totals[entry.date] ?? 0) + entry.duration_minutes;
    }
    return totals;
  }, [filtered]);

  const formatDuration = (minutes: number) => {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${h}h ${m}m`;
  };

  const uniqueDates = [...new Set(filtered.map((e) => e.date))];

  return (
    <div>
      <PageHeader
        title="Time Tracking"
        description="Track time spent on tasks and projects"
        action={
          <Button onClick={() => setShowManualEntry(true)}>
            <Plus className="mr-2 h-4 w-4" /> Manual Entry
          </Button>
        }
      />

      {/* Timer Widget */}
      <TimerWidget projects={projects ?? []} onTimerStopped={() => refetch()} />

      {/* Filters */}
      <Card className="my-6">
        <CardContent className="flex flex-wrap items-end gap-4 pt-4">
          <div className="space-y-1">
            <Label className="text-xs">Project</Label>
            <Select value={projectFilter} onValueChange={setProjectFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Projects</SelectItem>
                {(projects ?? []).map((p) => (
                  <SelectItem key={p.id} value={p.title}>
                    {p.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">From</Label>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-[160px]"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">To</Label>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-[160px]"
            />
          </div>
        </CardContent>
      </Card>

      {error && <ErrorAlert message={String(error)} onRetry={() => refetch()} />}

      {isLoading && (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </div>
      )}

      {!isLoading && filtered.length === 0 && (
        <EmptyState
          icon={Clock}
          title="No time entries"
          description="Start the timer or add a manual entry."
          actionLabel="Add Manual Entry"
          onAction={() => setShowManualEntry(true)}
        />
      )}

      {filtered.length > 0 && (
        <div className="rounded-lg border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-4 py-3 text-left font-medium">Date</th>
                <th className="px-4 py-3 text-left font-medium">Duration</th>
                <th className="px-4 py-3 text-left font-medium">Task</th>
                <th className="px-4 py-3 text-left font-medium">Project</th>
                <th className="px-4 py-3 text-left font-medium">Description</th>
              </tr>
            </thead>
            <tbody>
              {uniqueDates.map((date) => {
                const dayEntries = filtered.filter((e) => e.date === date);
                return (
                  <DayGroup
                    key={date}
                    entries={dayEntries}
                    total={dailyTotals[date] ?? 0}
                    formatDuration={formatDuration}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <ManualEntryDialog
        open={showManualEntry}
        onOpenChange={setShowManualEntry}
        projects={projects ?? []}
        onCreated={() => refetch()}
      />
    </div>
  );
}

function DayGroup({
  entries,
  total,
  formatDuration,
}: {
  entries: TimeEntry[];
  total: number;
  formatDuration: (m: number) => string;
}) {
  return (
    <>
      {entries.map((entry) => (
        <tr key={entry.id} className="border-b last:border-b-0">
          <td className="px-4 py-3 text-muted-foreground">
            {new Date(entry.date).toLocaleDateString()}
          </td>
          <td className="px-4 py-3 tabular-nums font-medium">
            {formatDuration(entry.duration_minutes)}
          </td>
          <td className="px-4 py-3">{entry.task_title ?? '-'}</td>
          <td className="px-4 py-3">{entry.project_title ?? '-'}</td>
          <td className="px-4 py-3 text-muted-foreground">{entry.description ?? '-'}</td>
        </tr>
      ))}
      <tr className="border-b bg-muted/30 last:border-b-0">
        <td className="px-4 py-2 text-xs font-semibold">
          Daily Total
        </td>
        <td className="px-4 py-2 text-xs font-semibold tabular-nums">
          {formatDuration(total)}
        </td>
        <td colSpan={3} />
      </tr>
    </>
  );
}
